import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { CalendarIcon, MapPin, User, Upload, X } from 'lucide-react';
import { format } from 'date-fns';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import { createStudentProfile, getMyStudentProfile, updateStudentProfile } from '../../services/authApi';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: 28.6139, // Delhi
  lng: 77.2090
};

const libraries = ['places'];

export const EnhancedStudentProfile = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(editMode);
  const [date, setDate] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    dob: '',
    parent_name: '',
    parent_mobile: '',
    school_name: '',
    grade: '',
    board: 'cbse',
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: null,
    longitude: null,
    photo_url: ''
  });

  // Fetch existing profile data in edit mode
  useEffect(() => {
    if (editMode && token) {
      fetchExistingProfile();
    }
  }, [editMode, token]);

  const fetchExistingProfile = async () => {
    try {
      setFetchingProfile(true);
      const profile = await getMyStudentProfile();

      // Populate form data
      setFormData({
        full_name: profile.full_name || '',
        username: profile.username || '',
        dob: profile.dob || '',
        parent_name: profile.parent_name || '',
        parent_mobile: profile.parent_mobile || '',
        school_name: profile.school_name || '',
        grade: profile.grade || '',
        board: profile.board || 'cbse',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        pincode: profile.pincode || '',
        latitude: profile.latitude || null,
        longitude: profile.longitude || null,
        photo_url: profile.photo_url || ''
      });

      // Set date if exists
      if (profile.dob) {
        setDate(new Date(profile.dob));
      }

      // Set photo preview
      if (profile.photo_url) {
        setPhotoPreview(profile.photo_url);
      }

      // Set map position
      if (profile.latitude && profile.longitude) {
        const position = { lat: profile.latitude, lng: profile.longitude };
        setMapCenter(position);
        setMarkerPosition(position);
      }

      toast.success('Profile loaded successfully');
    } catch (error) {
      console.log('No existing profile found:', error);
      if (editMode) {
        toast.info('No existing profile found. Create a new one.');
      }
    } finally {
      setFetchingProfile(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBoardChange = (value) => {
    setFormData({ ...formData, board: value });
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setFormData({ ...formData, dob: format(selectedDate, 'yyyy-MM-dd') });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) { // 500KB limit
        toast.error('Image size should be less than 500KB. Please compress your image before uploading.');
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData({ ...formData, photo_url: '' });
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);

      const data = await apiClient.post('/upload/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return data.url;
    } catch (error) {
      console.error('Photo upload error:', error);
      // If upload fails, use the preview as base64
      return photoPreview;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarkerPosition({ lat, lng });
    setFormData({
      ...formData,
      latitude: lat,
      longitude: lng
    });

    // Reverse geocode to get address
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const addressComponents = results[0].address_components;
        const formattedAddress = results[0].formatted_address;

        let city = '', state = '', pincode = '';

        addressComponents.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            pincode = component.long_name;
          }
        });

        setFormData(prev => ({
          ...prev,
          address: formattedAddress,
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode,
          latitude: lat,
          longitude: lng
        }));
      }
    });
  }, [formData]);

  const onAutocompleteLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });

        const addressComponents = place.address_components || [];
        let city = '', state = '', pincode = '';

        addressComponents.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            pincode = component.long_name;
          }
        });

        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode,
          latitude: lat,
          longitude: lng
        }));
      }
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setFormData({
            ...formData,
            latitude: lat,
            longitude: lng
          });

          toast.success('Location captured successfully!');
        },
        (error) => {
          toast.error('Unable to fetch your location');
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload photo first if exists
      let photoUrl = formData.photo_url;
      if (photoFile) {
        photoUrl = await uploadPhoto();
      }

      const profileData = {
        ...formData,
        photo_url: photoUrl,
        show_map: showMap
      };

      if (editMode) {
        await updateStudentProfile(profileData);
        toast.success('Profile updated successfully!');
      } else {
        await createStudentProfile(profileData);
        toast.success('Profile created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} profile`);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'student') {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-3 rounded-lg">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">{editMode ? 'Edit' : 'Create'} Student Profile</CardTitle>
                <CardDescription>{editMode ? 'Update your profile information' : 'Complete your profile to get started with Avyudha'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Photo Upload Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile Photo</h3>
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    {photoPreview ? (
                      <div className="relative">
                        <img
                          src={photoPreview}
                          alt="Profile preview"
                          className="w-32 h-32 rounded-full object-cover border-4 border-blue-100"
                        />
                        <button
                          type="button"
                          onClick={removePhoto}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-16 w-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="photo" className="cursor-pointer">
                      <div className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        <Upload className="h-4 w-4" />
                        <span>Upload Photo</span>
                      </div>
                      <Input
                        id="photo"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </Label>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF (max 500KB)</p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label>Date of Birth *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={handleDateSelect}
                          initialFocus
                          disabled={(date) => date > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Parent Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Parent Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="parent_name">Parent Name *</Label>
                    <Input
                      id="parent_name"
                      name="parent_name"
                      value={formData.parent_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="parent_mobile">Parent Mobile *</Label>
                    <Input
                      id="parent_mobile"
                      name="parent_mobile"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={formData.parent_mobile}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Academic Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="school_name">School Name *</Label>
                    <Input
                      id="school_name"
                      name="school_name"
                      value={formData.school_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="grade">Class/Grade *</Label>
                    <Select value={formData.grade} onValueChange={(value) => setFormData({ ...formData, grade: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select class" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i + 1} value={`${i + 1}`}>Class {i + 1}</SelectItem>
                        ))}
                        <SelectItem value="11th">11th</SelectItem>
                        <SelectItem value="12th">12th</SelectItem>
                        <SelectItem value="graduation">Graduation</SelectItem>
                        <SelectItem value="post_graduation">Post Graduation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label>Board *</Label>
                    <Select value={formData.board} onValueChange={handleBoardChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cbse">CBSE</SelectItem>
                        <SelectItem value="icse">ICSE</SelectItem>
                        <SelectItem value="state_board">State Board</SelectItem>
                        <SelectItem value="international_baccalaureate">International Baccalaureate (IB)</SelectItem>
                        <SelectItem value="igcse">IGCSE</SelectItem>
                        <SelectItem value="none">None of the above</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Location with Google Maps */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Location Information</h3>

                <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                  <div className="space-y-4">
                    {/* Address Autocomplete */}
                    <div>
                      <Label>Search Address</Label>
                      <Autocomplete
                        onLoad={onAutocompleteLoad}
                        onPlaceChanged={onPlaceChanged}
                      >
                        <Input placeholder="Type to search address..." />
                      </Autocomplete>
                    </div>

                    {/* Current Location Button */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={getCurrentLocation}
                      className="w-full md:w-auto"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Use Current Location
                    </Button>

                    {/* Map */}
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={13}
                      onClick={onMapClick}
                    >
                      {markerPosition && <Marker position={markerPosition} />}
                    </GoogleMap>

                    {/* Address Fields */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="address">Address *</Label>
                        <Input
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Street address"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="pincode">Pincode</Label>
                        <Input
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          placeholder="110001"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="show_map"
                        checked={showMap}
                        onChange={(e) => setShowMap(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="show_map" className="font-normal">
                        Show my location on map (visible to tutors)
                      </Label>
                    </div>

                    {formData.latitude && formData.longitude && (
                      <p className="text-sm text-green-600">
                        Location captured: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                      </p>
                    )}
                  </div>
                </LoadScript>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || uploadingPhoto}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Creating Profile...' : uploadingPhoto ? 'Uploading Photo...' : 'Create Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
